/**
 * Analyst Workspace
 *
 * Custom 3-column productivity view for analysts with apprenticeship tracking
 */
import {
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Avatar,
  TextInput,
  RingProgress
} from '@mantine/core';
import TopNav from '../components/TopNav';

export default function AnalystWorkspace() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#f8f9fa'
    }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Content area with 3-column layout */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '280px 1fr 320px',
        overflow: 'hidden'
      }}>
        {/* Left Navbar - Apprenticeship Progress */}
        <div style={{
        background: 'white',
        borderRight: '1px solid #dee2e6',
        overflowY: 'auto',
        padding: 16
      }}>
        <Stack gap="md">
          <Text size="md" fw={600}>Apprenticeship Progress</Text>

          {/* Progress Circle */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <RingProgress
              size={120}
              thickness={8}
              roundCaps
              sections={[{ value: 70, color: 'violet' }]}
              label={
                <Text size="xl" fw={700} c="violet" ta="center">
                  70%
                </Text>
              }
            />
          </div>

          {/* Milestones */}
          <Stack gap={12}>
            <Group gap="sm" wrap="nowrap">
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#40c057',
                border: '2px solid #40c057',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
                flexShrink: 0
              }}>✓</div>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>Foundation Training</Text>
                <Text size="xs" c="dimmed">Completed Mar 2024</Text>
              </div>
            </Group>

            <Group gap="sm" wrap="nowrap">
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#40c057',
                border: '2px solid #40c057',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
                flexShrink: 0
              }}>✓</div>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>First FI Review</Text>
                <Text size="xs" c="dimmed">Completed Jun 2024</Text>
              </div>
            </Group>

            <Group gap="sm" wrap="nowrap">
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#40c057',
                border: '2px solid #40c057',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
                flexShrink: 0
              }}>✓</div>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>Risk Assessment Cert</Text>
                <Text size="xs" c="dimmed">Completed Sep 2024</Text>
              </div>
            </Group>

            <Group gap="sm" wrap="nowrap">
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'white',
                border: '2px solid #dee2e6',
                flexShrink: 0
              }}></div>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>Lead Analysis</Text>
                <Text size="xs" c="dimmed">In Progress - 60%</Text>
              </div>
            </Group>

            <Group gap="sm" wrap="nowrap">
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'white',
                border: '2px solid #dee2e6',
                flexShrink: 0
              }}></div>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>Final Assessment</Text>
                <Text size="xs" c="dimmed">Scheduled Q1 2026</Text>
              </div>
            </Group>
          </Stack>

          {/* Daily Learning */}
          <Text size="md" fw={600} mt="lg">Today's Learning</Text>

          <div style={{
            background: 'linear-gradient(135deg, #f3f0ff 0%, #f8f9fa 100%)',
            border: '1px solid #c0b3f0',
            borderRadius: 8,
            padding: 12,
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <Badge size="xs" color="violet" mb={4}>NEW</Badge>
            <Text size="sm" fw={600} mb={4}>E-23 Advanced Concepts</Text>
            <Text size="xs" c="dimmed" mb={4}>Model explainability techniques</Text>
            <Text size="xs" c="dimmed">⏱ 45 min</Text>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f3f0ff 0%, #f8f9fa 100%)',
            border: '1px solid #c0b3f0',
            borderRadius: 8,
            padding: 12,
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <Badge size="xs" color="orange" mb={4}>RECOMMENDED</Badge>
            <Text size="sm" fw={600} mb={4}>Capital Adequacy Deep Dive</Text>
            <Text size="xs" c="dimmed" mb={4}>Basel IV implications</Text>
            <Text size="xs" c="dimmed">⏱ 30 min</Text>
          </div>

          {/* Mentor Connection */}
          <Text size="md" fw={600} mt="lg">Mentor</Text>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            background: 'white',
            border: '1px solid #e9ecef',
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f8f9fa';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.transform = 'translateX(0)';
          }}>
            <div style={{ position: 'relative' }}>
              <Avatar size={36} radius="xl" color="gray">SC</Avatar>
              <div style={{
                width: 8,
                height: 8,
                background: '#40c057',
                borderRadius: '50%',
                position: 'absolute',
                bottom: 0,
                right: 0,
                border: '2px solid white'
              }}></div>
            </div>
            <div>
              <Text size="sm" fw={600}>Sarah Chen</Text>
              <Text size="xs" c="dimmed">Lead Supervisor • Available</Text>
            </div>
          </div>
        </Stack>
      </div>

        {/* Main Content - Active Analysis Workspace */}
        <div style={{
          overflowY: 'auto',
          padding: 16
        }}>
        <Stack gap="md">
          {/* Current Assignment */}
          <Card withBorder padding="md">
            <Group justify="space-between" mb="sm">
              <div>
                <Text size="md" fw={600} mb={4}>Q3 Capital Analysis - Mid-Size Banks</Text>
                <Text size="xs" c="dimmed">Supporting Sarah Chen • Due Nov 15, 2025</Text>
              </div>
              <Badge color="blue" size="sm">IN PROGRESS</Badge>
            </Group>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
              marginBottom: 16
            }}>
              <div>
                <Text size="xs" tt="uppercase" c="dimmed" mb={4}>Banks Analyzed</Text>
                <Text size="xl" fw={700}>5/8</Text>
              </div>
              <div>
                <Text size="xs" tt="uppercase" c="dimmed" mb={4}>Reports Reviewed</Text>
                <Text size="xl" fw={700}>23</Text>
              </div>
              <div>
                <Text size="xs" tt="uppercase" c="dimmed" mb={4}>Issues Found</Text>
                <Text size="xl" fw={700} c="orange">3</Text>
              </div>
              <div>
                <Text size="xs" tt="uppercase" c="dimmed" mb={4}>Time Spent</Text>
                <Text size="xl" fw={700}>14h</Text>
              </div>
            </div>

            <Group gap="sm">
              <Button size="sm" color="violet">Continue Analysis</Button>
              <Button size="sm" variant="default">View Draft Report</Button>
              <Button size="sm" variant="default">Request Review</Button>
            </Group>
          </Card>

          {/* Analysis Tools */}
          <Text size="md" fw={600}>Analysis Toolkit</Text>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8
          }}>
            {[
              { icon: '📊', label: 'Ratio Calculator' },
              { icon: '📈', label: 'Trend Analyzer' },
              { icon: '🔍', label: 'Peer Compare' },
              { icon: '📑', label: 'Report Builder' },
              { icon: '⚠️', label: 'Risk Scorer' },
              { icon: '📝', label: 'Templates' }
            ].map((tool) => (
              <div key={tool.label} style={{
                padding: 16,
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.borderColor = '#7950f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#e9ecef';
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  margin: '0 auto 8px',
                  background: '#7950f2',
                  color: 'white',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14
                }}>{tool.icon}</div>
                <Text size="xs" fw={600}>{tool.label}</Text>
              </div>
            ))}
          </div>

          {/* Current Data View */}
          <Text size="md" fw={600}>Analysis Data - Capital Ratios Q3 2025</Text>
          <Card withBorder padding="md">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>Institution</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>CET1 Ratio</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>Tier 1</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>Total Capital</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>Leverage</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#495057', fontWeight: 600 }}>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'National Bank', cet1: '11.8%', tier1: '13.2%', total: '15.1%', leverage: '4.3%', trend: '↑', trendColor: '#40c057' },
                    { name: 'Laurentian Bank', cet1: '10.2%', tier1: '11.5%', total: '13.8%', leverage: '3.9%', trend: '→', trendColor: '#fd7e14' },
                    { name: 'Tangerine', cet1: '12.5%', tier1: '12.5%', total: '14.2%', leverage: '4.1%', trend: '↑', trendColor: '#40c057' },
                    { name: 'Manulife Bank', cet1: '13.1%', tier1: '13.1%', total: '15.6%', leverage: '4.5%', trend: '↑', trendColor: '#40c057' },
                    { name: 'Canadian Western', cet1: '9.8%', tier1: '11.2%', total: '13.4%', leverage: '3.7%', trend: '↓', trendColor: '#fa5252' }
                  ].map((bank) => (
                    <tr key={bank.name} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>{bank.name}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13 }}>{bank.cet1}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13 }}>{bank.tier1}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13 }}>{bank.total}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13 }}>{bank.leverage}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13, color: bank.trendColor }}>{bank.trend}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{
              marginTop: 16,
              padding: 12,
              background: '#fff4e6',
              borderRadius: 4
            }}>
              <Text size="xs" c="orange" fw={600}>
                ⚠ Canadian Western Bank approaching minimum CET1 threshold (10.5%)
              </Text>
            </div>
          </Card>
        </Stack>
      </div>

        {/* Right Aside - Context-Aware Guidance */}
        <div style={{
          background: 'white',
          borderLeft: '1px solid #dee2e6',
          overflowY: 'auto',
          padding: 16
        }}>
        <Stack gap="md">
          {/* Context-Aware Help */}
          <Text size="md" fw={600}>Guidance for Current Task</Text>

          <div style={{
            background: '#fffbf0',
            border: '1px solid #ffe066',
            borderRadius: 8,
            padding: 12
          }}>
            <Group gap="xs" align="flex-start">
              <div style={{
                width: 24,
                height: 24,
                background: '#fd7e14',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                flexShrink: 0
              }}>💡</div>
              <div>
                <Text size="sm" fw={600} mb={4}>Capital Analysis Tips</Text>
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                  When CET1 falls below 10%, check for:<br />
                  • Recent acquisitions<br />
                  • Dividend policies<br />
                  • Risk-weighted asset growth
                </Text>
              </div>
            </Group>
          </div>

          <div style={{
            background: '#fffbf0',
            border: '1px solid #ffe066',
            borderRadius: 8,
            padding: 12
          }}>
            <Group gap="xs" align="flex-start">
              <div style={{
                width: 24,
                height: 24,
                background: '#228be6',
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                flexShrink: 0
              }}>📚</div>
              <div>
                <Text size="sm" fw={600} mb={4}>Relevant Guideline</Text>
                <Text size="xs" c="dimmed">A-4: Capital Adequacy Requirements</Text>
                <Text size="xs" c="dimmed" mt={4}>Section 2.3 - Minimum ratios</Text>
              </div>
            </Group>
          </div>

          {/* Expert Directory */}
          <Text size="md" fw={600} mt="lg">Ask an Expert</Text>
          <TextInput placeholder="Search experts..." size="sm" mb="sm" />

          <Stack gap="xs">
            {[
              { initials: 'MJ', name: 'Michael Jung', role: 'Capital Markets • 12y exp', online: true },
              { initials: 'LP', name: 'Lisa Park', role: 'Risk Assessment • 8y exp', online: true },
              { initials: 'RM', name: 'Robert Mills', role: 'Basel Expert • Offline', online: false }
            ].map((expert) => (
              <div key={expert.initials} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateX(0)';
              }}>
                <div style={{ position: 'relative' }}>
                  <Avatar size={36} radius="xl" color="gray">{expert.initials}</Avatar>
                  <div style={{
                    width: 8,
                    height: 8,
                    background: expert.online ? '#40c057' : '#868e96',
                    borderRadius: '50%',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    border: '2px solid white'
                  }}></div>
                </div>
                <div>
                  <Text size="sm" fw={600}>{expert.name}</Text>
                  <Text size="xs" c="dimmed">{expert.role}</Text>
                </div>
              </div>
            ))}
          </Stack>

          {/* Team Q&A Feed */}
          <Text size="md" fw={600} mt="lg">Recent Team Q&A</Text>

          {[
            { q: 'How to calculate stressed LCR?', a: 'Use 30-day stressed outflow assumptions per B-6 guideline section 4.2...', author: 'M. Jung', time: '2h ago' },
            { q: 'E-23 validation requirements?', a: 'All models need independent validation covering soundness, accountability, and explainability...', author: 'L. Park', time: '5h ago' },
            { q: 'Peer bank comparison sources?', a: 'Use quarterly industry data and public financial statements for comprehensive view...', author: 'S. Chen', time: '1d ago' }
          ].map((qa, idx) => (
            <div key={idx} style={{
              padding: 12,
              background: '#f8f9fa',
              borderRadius: 4,
              marginBottom: 8
            }}>
              <Text size="sm" fw={600} mb={4}>{qa.q}</Text>
              <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }} mb={4}>{qa.a}</Text>
              <Text size="xs" c="dimmed">Answered by {qa.author} • {qa.time}</Text>
            </div>
          ))}
          </Stack>
        </div>
      </div>
    </div>
  );
}
